import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { AiFillDollarCircle } from "react-icons/ai";
import userResources from "api/userResources";
import DateFilter from "components/DateFilter/DateFilter";
import ScrollToTopOnRouteChange from "components/ScrollToTopOnRouteChange/ScrollToTopOnRouteChange";
import Widget from "components/widget/Widget";

// ---------- Helpers ----------
const padTo2Digits = (num) => String(num).padStart(2, "0");

const formatDate = (date) =>
  `${date.getFullYear()}-${padTo2Digits(date.getMonth() + 1)}-${padTo2Digits(
    date.getDate()
  )}`;

const DateInput = ({ label, selectedDate, setSelectedDate, ...props }) => (
  <label className="flex items-end justify-between gap-5 whitespace-nowrap text-sm font-medium text-gray-900 sm:flex-col sm:items-start sm:gap-0">
    {label}:
    <DateFilter
      selectedDate={selectedDate}
      setSelectedDate={setSelectedDate}
      {...props}
    />
  </label>
);

// ---------- Widget Types ----------
const widgetConfig = [
  {
    label: "Total",
    key: "total",
    delay: 0.1,
    bg: "bg-blue-500",
    text: "text-black",
    iconColor: "text-black",
  },
  {
    label: "Paid",
    key: "totalPaid",
    delay: 0.2,
    bg: "bg-green-500",
    text: "text-black",
    iconColor: "text-black",
  },
  {
    label: "Pending",
    key: "pendingAmount",
    delay: 0.3,
    bg: "bg-yellow-400",
    text: "text-black",
    iconColor: "text-black",
  },
];

export default function InvoiceStatics() {
  const location = useLocation();

  const [staticData, setStaticData] = useState(null);
  const [invoiceStats, setInvoiceStats] = useState({});
  console.log(staticData,"staticData");

  const [startDate, setStartDate] = useState(() => {
    const currentDate = new Date();
    currentDate.setDate(1);
    return formatDate(currentDate);
  });

  const [endDate, setEndDate] = useState(() => {
    const currentDate = new Date();
    currentDate.setMonth(currentDate.getMonth() + 1);
    currentDate.setDate(0);
    return formatDate(currentDate);
  });

  // ---------- API call ----------
  const getInvoiceStatics = useCallback(async () => {
    try {
      const params = {};

      if (startDate) {
        const d = new Date(startDate);
        d.setDate(d.getDate() - 1); // subtract 1 day
        params.start_date = formatDate(d);
      }

      if (endDate) {
        const d = new Date(endDate);
        d.setDate(d.getDate() + 1); // add 1 day
        params.end_date = formatDate(d);
      }

      const response = await userResources.invoiceStatics(params);
      setInvoiceStats(response?.data?.data || {});
    } catch (error) {
      console.error("Error fetching invoice statistics:", error);
    }
  }, [startDate, endDate]);

  // ---------- Effects ----------
  useEffect(() => {
    setStaticData(location?.state || null);
  }, [location?.state]);

  useEffect(() => {
    getInvoiceStatics();
  }, [getInvoiceStatics]);

  return (
    <div>
      <ScrollToTopOnRouteChange />
      <h1 className="my-2 text-2xl font-bold capitalize text-gray-900">
        {staticData?.airticket
          ? "Air Ticket"
          : staticData?.insurance
          ? "Insurance"
          : staticData?.landPackage
          ? "Land Package"
          : staticData?.cruise
          ? "Cruise"
          : staticData?.hotel
          ? "Hotel"
          : ""}
      </h1>
      {/* Date Filters */}
      <div className="grid grid-cols-1 gap-4 rounded-[20px] bg-gray-300 p-2 dark:bg-white sm:grid-cols-2">
        <DateInput
          label="Start Date"
          selectedDate={startDate}
          setSelectedDate={setStartDate}
          maxDate={endDate}
        />
        <DateInput
          label="End Date"
          selectedDate={endDate}
          setSelectedDate={setEndDate}
          minDate={startDate}
        />
      </div>

      {/* Widgets */}
      <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {Object.entries(invoiceStats).map(([key, value], index) => {
          if (!staticData?.[key] || !value?.enabled) return null;

          return widgetConfig.map((type) => (
            <motion.div
              key={`${key}-${type.label}`}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.5,
                delay: index * 0.1 + type.delay,
              }}
            >
              <Widget
                icon={
                  <AiFillDollarCircle className={`h-7 w-7 ${type.iconColor}`} />
                }
                heading={`${type.label}`}
                subtitle={`$${Number(value[type.key]).toLocaleString()}`}
                cardbgColor={type.bg}
                titleColor={type.text}
              />
            </motion.div>
          ));
        })}
      </div>
    </div>
  );
}
